<template>
  <div class="app-page">
    <Header helpURL="studio/apps/">
      <template #page-slot>
        Apps
      </template>
    </Header>

    <div class="main-page-div">
      <main class="main-display-area">
        <div class="apps-page__content scrollbar">
          <!-- Search filters — always visible -->
          <div class="apps-page__filters">
            <FilterEntry
              label="Name"
              type="text"
              :defaultValue="searchName"
              @filter="onNameFilter"
            />
            <FilterEntry
              label="Title"
              type="text"
              :defaultValue="searchTitle"
              @filter="onTitleFilter"
            />
            <FilterEntry
              label="Author"
              type="text"
              :defaultValue="searchAuthor"
              @filter="onAuthorFilter"
            />
            <FilterEntry
              label="Type"
              type="dropdown"
              :defaultValue="filter || ''"
              :options="availableTypes.map((t) => ({ label: t.type.charAt(0).toUpperCase() + t.type.slice(1), value: t.type }))"
              @filter="onTypeFilterChange"
            />
          </div>

          <!-- Type cards view -->
          <AppsTypesList
            v-if="!filter && !searchName && !searchTitle && !searchAuthor"
            :appsSummary="appsSummary"
            :loading="summaryLoading"
            :searchName="searchName"
            :searchTitle="searchTitle"
            :searchAuthor="searchAuthor"
            @filter="onCardFilter"
          />

          <AppsList
            v-if="filter || searchName || searchTitle || searchAuthor"
            :filterType="filter"
            :searchName="searchName"
            :searchTitle="searchTitle"
            :searchAuthor="searchAuthor"
            @job_started="handleJobStarted"
          />

        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import Header from '@/components/Header.vue';
import AppsList from '@/components/Apps/AppsList.vue';
import AppsTypesList from '@/components/Apps/AppsTypesList.vue';
import FilterEntry from '@/components/FilterEntry.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { getFrontendAppsSummary } from '@/assets/js/apps/apps-manager';

const router = useRouter();
const dataConnect = new DatastoreConnect();

const searchName = ref('');
const searchTitle = ref('');
const searchAuthor = ref('');
const appsSummary = ref(null);
const summaryLoading = ref(false);
const filter = ref(null);

const availableTypes = computed(() => appsSummary.value?.types ?? []);

function onTypeFilterChange(val) {
  filter.value = val || null;
}

function onCardFilter(payload) {
  filter.value = payload?.type || null;
  searchAuthor.value = payload?.author || '';
}

function onNameFilter(val) {
  searchName.value = val || '';
}

function onTitleFilter(val) {
  searchTitle.value = val || '';
}

function onAuthorFilter(val) {
  searchAuthor.value = val || '';
}

const capitalizedFilter = computed(() => {
  const f = String(filter.value || '');
  return f.charAt(0).toUpperCase() + f.slice(1);
});

function clearFilter() {
  filter.value = null;
}

function handleJobStarted() {
  router.push({ name: 'tasks' });
}

function mergeAppsSummary(backendSummary = {}, frontendSummary = {}) {
  const mergedByType = new Map();

  for (const entry of backendSummary?.types ?? []) {
    mergedByType.set(entry.type, {
      ...entry,
      authors: Array.isArray(entry.authors) ? [...entry.authors] : [],
      count: entry.count ?? 0,
      free: entry.free ?? 0,
      description: entry.description ?? '',
    });
  }

  for (const entry of frontendSummary?.types ?? []) {
    const existing = mergedByType.get(entry.type);

    if (!existing) {
      mergedByType.set(entry.type, {
        ...entry,
        authors: Array.isArray(entry.authors) ? [...entry.authors] : [],
        count: entry.count ?? 0,
        free: entry.free ?? 0,
        description: entry.description ?? '',
      });
      continue;
    }

    existing.count += entry.count ?? 0;
    existing.free += entry.free ?? 0;
    existing.authors = Array.from(new Set([
      ...(existing.authors ?? []),
      ...(entry.authors ?? []),
    ]));
    existing.description = existing.description || entry.description || '';
  }

  return {
    ...backendSummary,
    types: Array.from(mergedByType.values()),
  };
}

async function loadSummary() {
  summaryLoading.value = true;
  try {
    const resp = await dataConnect.getAppsSummary();
    const backendSummary = resp?.result ?? { types: [] };
    const frontendSummary = getFrontendAppsSummary();

    appsSummary.value = mergeAppsSummary(backendSummary, frontendSummary);
  } catch (e) {
    console.error('Failed to load apps summary:', e);
    appsSummary.value = getFrontendAppsSummary();
  } finally {
    summaryLoading.value = false;
  }
}

onMounted(() => {
  loadSummary();
});
</script>

<style lang="scss" scoped>
.app-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.main-page-div {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.main-display-area {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.apps-page__content {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 20px 24px;
  overflow-y: auto;
  gap: 20px;
}

.apps-page__filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.apps-page__back-btn {
  font-size: 0.82rem;
  font-weight: 600;
  border: none;
  background: none;
  cursor: pointer;
  padding: 4px 0;
  @include themify() {
    color: #250E81;
  }
  &:hover {
    text-decoration: underline;
  }
}

.apps-page__filter-label {
  font-size: 0.95rem;
  font-weight: 700;
}

.apps-page__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}

.apps-page__filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 200px;
  flex: 1;
  max-width: 320px;
}

.apps-page__label {
  font-size: 0.78rem;
  font-weight: 600;
  @include themify() {
    color: #555;
  }
}

.apps-page__input {
  height: 32px;
  padding: 0 10px;
  border-radius: 4px;
  font-size: 0.85rem;
  outline: none;
  @include themify() {
    border: 1px solid #d0d0d0;
    background: #ffffff;
    color: #333;
  }
  &:focus {
    @include themify() {
      border-color: #250E81;
    }
  }
}

.apps-page__select {
  cursor: pointer;
}

</style>
